import React, {useCallback, useEffect, useState} from 'react';
import {
  Button,
  ChevronDownSmallIcon,
  PopOut,
  Vertical,
  Horizontal,
  RangeInput,
  Theme,
  ThemeName,
  ZoomOutIcon,
  ButtonWithPopout,
} from '@taopix/taopix-design-system';
import {useUiState} from '../Context/UserInterface/UserInterfaceContext';
import classNames from 'classnames';
import {ViewMode, ZOOM_BASE_LEVEL, ZOOM_MAX_LEVEL, DEFAULT_ZOOM} from '@taopix/taopix-core';
import {useTaopix} from '../Context/TaopixContext';
import {UserInterfaceActions} from '../Context/UserInterface/UserInterfaceActionTypes';
import {useTranslation} from 'react-i18next';
import {useExperience} from '../Experience/ExperienceProvider';
import {useSelector} from 'react-redux';
import {LayoutSelector} from '../../Selectors';
import useViewMode from '../../Hooks/useViewMode';

const ZoomControl = () => {
  const app = useTaopix();
  const ui = useUiState();
  const {t} = useTranslation(['*']);
  const {experience} = useExperience();
  const [viewMode] = useViewMode();
  const [zoomLevelPopoutOpen, setZoomLevelPopoutOpen] = useState(false);
  const [internalZoomLevel, setInternalZoomLevel] = useState<number>(ui.state.zoomLevel);
  const layout = useSelector(LayoutSelector);

  const zoomToolbarClasses = classNames(
    'flex',
    'shadow-md',
    'overflow-hidden',
    internalZoomLevel > DEFAULT_ZOOM && [
      'outline',
      'outline-themeAccentColour',
      'outline-[max(2rem,var(--tds-theme-border-size))]',
      'outline-offset-[calc(0rem-var(--tds-theme-border-size))]',
    ]
  );

  const smallScreenResetButtonClasses = classNames(
    'absolute',
    'right-0',
    'flex',
    'z-10',
    'shadow-md'
  );

  const onSetZoomLevel = useCallback(
    (level: number, interacting?: boolean) => {
      // While sliding, update the canvas zoom level
      app.core.get('abilities').setZoom(level, false, false);

      // While sliding, update the internal zoom state so the dropdown shows the correct value
      setInternalZoomLevel(level);

      ui.dispatch(UserInterfaceActions.setZoomLevel(level));

      // Close the zoom popout
      if (zoomLevelPopoutOpen) {
        setZoomLevelPopoutOpen(false);
      }
    },
    [zoomLevelPopoutOpen]
  );

  useEffect(() => {
    setInternalZoomLevel(ui.state.zoomLevel);
  }, [ui.state.zoomLevel]);

  useEffect(() => {
    return () => {
      // When unmounting because the layout code changed, reset the zoom level.
      // We just check if there was a layout code to prevent re-renders before the
      // project was initialised, for example.
      if (layout?.code) {
        setInternalZoomLevel(DEFAULT_ZOOM);
      }
    };
  }, [layout?.code]);

  // Don't show the zoom control if it disabled in the experience or we're in grid mode
  if (
    (ViewMode.List === viewMode && !experience?.EDITOR?.workArea?.listView?.zoomControl) ||
    (ViewMode.Single === viewMode && !experience?.EDITOR?.workArea?.singlePage?.zoomControl) ||
    (ViewMode.Preview === viewMode &&
      (!experience?.EDITOR?.workArea?.previewMode?.zoomControl ||
        ViewMode.Grid === ui.state.previewViewMode)) ||
    ViewMode.Grid === viewMode
  ) {
    return <></>;
  }

  return (
    <>
      {!ui.state.isSmallScreen ? (
        <Theme name={ThemeName.Popouts} className={zoomToolbarClasses}>
          {internalZoomLevel > DEFAULT_ZOOM && (
            <Button
              label={t('str_LabelReset')}
              size={'small'}
              corners="square"
              onClick={() => onSetZoomLevel(DEFAULT_ZOOM)}
            />
          )}
          <ButtonWithPopout
            type="listbox"
            returnFocusToTriggerOnItemClick
            buttonProps={{
              id: 'zoomLevelButton',
              label: `${internalZoomLevel}%`,
              'aria-label': `${t('*:str_LabelZoomLevel', {
                defaultValue: 'Zoom level',
              })} (${internalZoomLevel}%)`,
              buttonStyle: 'standard',
              endIcon: <ChevronDownSmallIcon />,
              size: 'small',
              justified: true,
              className: 'mr-sm',
              corners: 'square',
            }}
            popoutProps={{
              'aria-label': t('*:str_LabelSelectZoomLevel', {defaultValue: 'Select zoom level'}),
            }}
          >
            {[100, 125, 150, 175, 200, 225, 250].map(n => {
              return (
                <Button
                  key={n}
                  label={`${n}%`}
                  buttonStyle={'standard'}
                  corners={'square'}
                  labelAlignment={'left'}
                  onClick={() => onSetZoomLevel(n)}
                  aria-pressed={internalZoomLevel === n}
                />
              );
            })}
          </ButtonWithPopout>
          <RangeInput
            min={ZOOM_BASE_LEVEL}
            max={ZOOM_MAX_LEVEL}
            step={5}
            value={internalZoomLevel}
            id="zoomSlider"
            onValueChange={(value: number) => {
              onSetZoomLevel(value);
            }}
            className={'flex-1 mr-sm'}
            isSmallScreen={ui.state.isSmallScreen}
            aria-label={t('*:str_LabelZoomSlider', {defaultValue: 'Zoom slider'})}
            aria-controls="workarea"
          />
        </Theme>
      ) : (
        // Show the reset zoom button on small screen when zoomed
        <>
          {internalZoomLevel > DEFAULT_ZOOM && (
            <Button
              label={t('str_LabelReset')}
              className={smallScreenResetButtonClasses}
              onClick={() => onSetZoomLevel(DEFAULT_ZOOM)}
              startIcon={<ZoomOutIcon />}
            />
          )}
        </>
      )}
    </>
  );
};

export default ZoomControl;
